import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PaperPlaneTilt, Eye, EnvelopeSimple, ChatCircle, ArrowLeft, CheckCircle, Warning } from "@phosphor-icons/react";
import DOMPurify from "dompurify";
import { api } from "../../lib/api";

const AUDIENCES = [
  { value: "all_applied", label: "Everyone who applied", sub: "Pending + Approved + Rejected", scope: "rsvp" },
  { value: "all_approved", label: "All approved", sub: "Confirmed attendees", scope: "rsvp" },
  { value: "guests", label: "Approved guests only", sub: "Excludes sponsor team / sponsor requests", scope: "rsvp" },
  { value: "sponsor_team", label: "Sponsor team only", sub: "Working for an event sponsor", scope: "rsvp" },
  { value: "sponsor_request", label: "Sponsor requests", sub: "Approved leads to sponsor a future event", scope: "rsvp" },
  { value: "all_subscribers", label: "Newsletter subscribers", sub: "Everyone on the mailing list — email only", scope: "subscribers", emailOnly: true },
];

const DEFAULT_HTML = `<div style="font-family:Helvetica,Arial,sans-serif;color:#111;max-width:560px;margin:0 auto;padding:24px">
  <h1 style="font-weight:900;font-size:28px;line-height:1.1;margin:0 0 16px">Quick update on FintechOmni Las Vegas 2027.</h1>
  <p style="font-size:16px;line-height:1.7">Hi there —</p>
  <p style="font-size:16px;line-height:1.7">Doors open at 2 PM at the venue. Bring your QR code and a photo ID.</p>
  <p style="font-size:16px;line-height:1.7">— FintechOmni</p>
</div>`;

const Composer = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({
    name: "",
    channel: "email",
    audience: "all_approved",
    event_id: "",
    subject: "",
    body_html: DEFAULT_HTML,
    body_text: "",
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(true);

  useEffect(() => {
    api.get("/admin/events").then(({ data }) => setEvents(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchPreview = async () => {
    setPreviewing(true);
    setError("");
    try {
      const { data } = await api.post("/admin/broadcasts/preview", form);
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load recipient count");
    } finally {
      setPreviewing(false);
    }
  };

  // Auto-refresh recipient count when audience/event changes
  useEffect(() => {
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.audience, form.event_id]);

  const sms = form.channel === "sms";
  const smsLen = (form.body_text || "").length;

  const handleSend = async () => {
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/admin/broadcasts", form);
      navigate(`/admin/broadcasts/${data.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed");
      setConfirmOpen(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="broadcast-composer">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <Link to="/admin/broadcasts" className="font-mono-tech text-xs text-cyber-yellow tracking-[0.2em] uppercase flex items-center gap-2"><ArrowLeft size={12} /> All broadcasts</Link>
          <div className="term-label mt-3">/ broadcasts · new</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">Compose.</h1>
          <p className="text-white/60 mt-2 text-sm font-mono-tech">Pre-event reminders, schedule changes, urgent updates.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* LEFT: form */}
        <div className="lg:col-span-7 space-y-4">
          <div className="hard-border bg-cyber-surface p-5">
            <div className="term-label mb-2">/ internal label</div>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Vegas — doors open reminder"
              className="w-full px-4 py-3 bg-cyber-black border border-cyber-line text-white"
              data-testid="broadcast-name"
            />
            <p className="font-mono-tech text-[10px] text-white/40 mt-2">Only visible to admins. Recipients never see this.</p>
          </div>

          <div className="hard-border bg-cyber-surface p-5">
            <div className="term-label mb-3">/ channel</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "email", l: "Email", icon: EnvelopeSimple, sub: "Postmark" },
                { v: "sms", l: "SMS", icon: ChatCircle, sub: "Infobip" },
              ].map((c) => {
                const Icon = c.icon;
                const sel = form.channel === c.v;
                return (
                  <button
                    key={c.v}
                    type="button"
                    onClick={() => set("channel", c.v)}
                    data-testid={`channel-${c.v}`}
                    className={`hard-border p-4 text-left transition-colors duration-150 ${sel ? "bg-white text-cyber-black border-white" : "bg-cyber-black border-cyber-line text-white hover:border-cyber-green"}`}
                  >
                    <div className="flex items-center gap-2"><Icon size={18} weight="bold" /><span className="font-mono-tech text-xs uppercase tracking-[0.15em]">{c.l}</span></div>
                    <div className={`font-mono-tech text-[10px] mt-2 uppercase tracking-[0.15em] ${sel ? "text-cyber-black/60" : "text-white/40"}`}>via {c.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="hard-border bg-cyber-surface p-5">
            <div className="term-label mb-3">/ event filter</div>
            <select
              value={form.event_id}
              onChange={(e) => set("event_id", e.target.value)}
              className="w-full px-3 py-2.5 bg-cyber-black border border-cyber-line text-white font-mono-tech text-xs uppercase tracking-[0.1em]"
              data-testid="broadcast-event"
            >
              <option value="">All events</option>
              {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
            </select>
          </div>

          <div className="hard-border bg-cyber-surface p-5">
            <div className="term-label mb-3">/ audience</div>
            <div className="space-y-2">
              {AUDIENCES.map((a) => {
                const sel = form.audience === a.value;
                const disabled = a.emailOnly && form.channel === "sms";
                const onClick = () => {
                  if (disabled) return;
                  set("audience", a.value);
                };
                return (
                  <button
                    key={a.value}
                    type="button"
                    onClick={onClick}
                    disabled={disabled}
                    data-testid={`audience-${a.value}`}
                    title={disabled ? "Newsletter subscribers only support email broadcasts" : undefined}
                    className={`w-full hard-border p-3 text-left transition-colors duration-150 ${
                      disabled
                        ? "bg-cyber-black border-cyber-line text-white/25 cursor-not-allowed"
                        : sel
                          ? "bg-white text-cyber-black border-white"
                          : "bg-cyber-black border-cyber-line text-white hover:border-cyber-green"
                    }`}
                  >
                    <div className="font-mono-tech text-xs uppercase tracking-[0.15em]">
                      {a.label}
                      {a.emailOnly && <span className="ml-2 font-mono-tech text-[9px] tracking-[0.2em] text-cyber-green">/ email only</span>}
                    </div>
                    <div className={`font-mono-tech text-[10px] mt-1 ${sel && !disabled ? "text-cyber-black/60" : "text-white/40"}`}>{a.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {form.channel === "email" && (
            <div className="hard-border bg-cyber-surface p-5 space-y-4">
              <div>
                <div className="term-label mb-2">/ subject</div>
                <input
                  value={form.subject}
                  onChange={(e) => set("subject", e.target.value)}
                  placeholder="Doors open at 2 PM today"
                  className="w-full px-4 py-3 bg-cyber-black border border-cyber-line text-white"
                  data-testid="broadcast-subject"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="term-label">/ html body</div>
                  <button type="button" onClick={() => setShowHtmlPreview((v) => !v)} className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-yellow hover:text-white" data-testid="toggle-html-preview">
                    {showHtmlPreview ? "Edit raw" : "Show preview"}
                  </button>
                </div>
                <textarea
                  rows={14}
                  value={form.body_html}
                  onChange={(e) => set("body_html", e.target.value)}
                  className="w-full px-4 py-3 bg-cyber-black border border-cyber-line text-white font-mono text-xs leading-relaxed"
                  data-testid="broadcast-html"
                />
              </div>
              <div>
                <div className="term-label mb-2">/ plain-text fallback (optional)</div>
                <textarea
                  rows={4}
                  value={form.body_text}
                  onChange={(e) => set("body_text", e.target.value)}
                  placeholder="Auto-generated from HTML if left empty."
                  className="w-full px-4 py-3 bg-cyber-black border border-cyber-line text-white font-mono text-xs"
                  data-testid="broadcast-text"
                />
              </div>
            </div>
          )}

          {form.channel === "sms" && (
            <div className="hard-border bg-cyber-surface p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="term-label">/ sms body</div>
                <span className={`font-mono-tech text-[10px] uppercase tracking-[0.2em] ${smsLen > 320 ? "text-cyber-red" : smsLen > 160 ? "text-cyber-yellow" : "text-white/50"}`} data-testid="sms-counter">
                  {smsLen} / 480 chars · {Math.ceil(Math.max(smsLen, 1) / 160)} segment{smsLen > 160 ? "s" : ""}
                </span>
              </div>
              <textarea
                rows={6}
                value={form.body_text}
                onChange={(e) => set("body_text", e.target.value)}
                placeholder="FintechOmni: doors open at 2 PM today at the venue. Bring QR + photo ID."
                className="w-full px-4 py-3 bg-cyber-black border border-cyber-line text-white"
                data-testid="broadcast-sms-text"
              />
              <p className="font-mono-tech text-[10px] text-white/40 mt-2">SMS over 160 chars splits into multiple segments and may not deliver to all carriers. Keep it under 480 chars.</p>
            </div>
          )}

          {error && (
            <div className="hard-border border-cyber-red text-cyber-red p-4 font-mono-tech text-xs flex items-center gap-2" data-testid="broadcast-error">
              <Warning size={14} weight="bold" /> {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={sending || !preview}
            className="btn-neon w-full sm:w-auto justify-center"
            data-testid="broadcast-send-btn"
          >
            <PaperPlaneTilt size={16} weight="bold" /> Review &amp; send to {preview?.recipient_count ?? "..."} recipient{(preview?.recipient_count || 0) === 1 ? "" : "s"}
          </button>
        </div>

        {/* RIGHT: preview */}
        <div className="lg:col-span-5 space-y-3">
          <div className="hard-border bg-cyber-surface p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="term-label">/ recipients</div>
              <button type="button" onClick={fetchPreview} disabled={previewing} className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-yellow hover:text-white">
                {previewing ? "..." : "refresh"}
              </button>
            </div>
            <div className="font-display font-black text-5xl tracking-tighter text-white" data-testid="recipient-count">{preview?.recipient_count ?? "—"}</div>
            <div className="font-mono-tech text-[10px] text-white/50 mt-1">deliverable {form.channel} {form.channel === "email" ? "addresses" : "numbers"}</div>
            {preview?.sample?.length > 0 && (
              <div className="mt-4 space-y-1">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40">/ sample (first 8)</div>
                {preview.sample.map((s, i) => (
                  <div key={s.email || s.phone || i} className="text-xs text-white/80 flex items-center justify-between gap-2 py-1 border-b border-cyber-line/40">
                    <span className="truncate">{s.name}</span>
                    <span className="font-mono-tech text-white/50 truncate">{form.channel === "email" ? s.email : s.phone}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {form.channel === "email" && showHtmlPreview && (
            <div className="hard-border bg-cyber-surface p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="term-label">/ email preview</div>
                <Eye size={12} className="text-white/40" />
              </div>
              <div className="bg-white text-cyber-black p-3 max-h-[460px] overflow-auto" data-testid="email-preview-frame">
                <div className="font-mono-tech text-[10px] text-cyber-black/60 mb-2 pb-2 border-b border-cyber-black/10">
                  Subject: <span className="text-cyber-black">{form.subject || "(no subject)"}</span>
                </div>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.body_html || "<em>(empty)</em>") }} />
              </div>
            </div>
          )}

          {form.channel === "sms" && (
            <div className="hard-border bg-cyber-surface p-5">
              <div className="term-label mb-3">/ sms preview</div>
              <div className="bg-cyber-black p-4">
                <div className="bg-cyber-elevated text-white p-4 max-w-[280px] text-sm leading-relaxed whitespace-pre-line break-words rounded-bl-2xl rounded-tl-2xl rounded-tr-2xl" data-testid="sms-preview">
                  {form.body_text || "(empty)"}
                </div>
                <div className="font-mono-tech text-[10px] text-white/40 mt-2">from FintechOmni</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-cyber-black/80 backdrop-blur-sm p-4" onClick={() => !sending && setConfirmOpen(false)} data-testid="confirm-modal">
          <div className="hard-border bg-cyber-surface w-full max-w-md p-7" onClick={(e) => e.stopPropagation()}>
            <Warning size={28} weight="duotone" className="text-cyber-yellow" />
            <h3 className="font-display font-black tracking-tighter text-2xl mt-4">Send to {preview?.recipient_count} recipients?</h3>
            <p className="text-white/70 mt-3 text-sm">
              You're about to send <strong className="text-white">{form.channel === "email" ? "email" : "SMS"}</strong> to{" "}
              <strong className="text-cyber-green">{preview?.recipient_count}</strong> {AUDIENCES.find((a) => a.value === form.audience)?.label.toLowerCase()}
              {form.event_id ? <> for <strong>{events.find((e) => e.id === form.event_id)?.title}</strong></> : null}.
            </p>
            <p className="text-white/50 text-xs mt-3 font-mono-tech">/ This action cannot be undone.</p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setConfirmOpen(false)} disabled={sending} className="btn-ghost-neon flex-1 justify-center" data-testid="cancel-send">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="btn-neon flex-1 justify-center" data-testid="confirm-send">
                {sending ? "Sending..." : <><CheckCircle size={14} weight="bold" /> Send now</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Composer;
